import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { NFeDto } from "../NFe.Dto";
import {
  NFeBuscarXML,
  NFeFiltrar,
  NFeImprimir,
  TributacaoBuscarPorId,
} from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import {
  FormatarCEP,
  FormatarCPF,
  FormatarCNPJ,
  FormatarDataHoraDefault,
  FormatarTelefone,
  MapearErrosServidor,
  SoNumeros,
} from "src/hook/Functions";
import { meioPagamento as listaMeiosPagamento } from "../../NFCe/NFCe.Dto";

const NFeVisualizaCode = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [nfe, setNfe] = useState<NFeDto | undefined>(location.state?.nfe);
  const [loading, setLoading] = useState(!nfe && !!id);
  const [downloadingXml, setDownloadingXml] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null);
  const [tributacaoNome, setTributacaoNome] = useState<string>(
    location.state?.nfe?.tributacaoNome || ""
  );

  const carregarTributacao = async (tributacaoId: number) => {
    try {
      const resp = await TributacaoBuscarPorId(tributacaoId.toString());
      if (resp.data.success && resp.data.data) {
        setTributacaoNome(resp.data.data.nome);
      }
    } catch {
      // Ignora erro
    }
  };

  useEffect(() => {
    if (nfe?.tributacaoNome) {
      setTributacaoNome(nfe.tributacaoNome);
    } else if (nfe?.tributacaoId && !tributacaoNome) {
      carregarTributacao(nfe.tributacaoId);
    }
  }, [nfe]);

  useEffect(() => {
    if (!nfe && id) {
      loadNFe(Number(id));
    }
  }, [id, nfe]);

  const loadNFe = async (nfeId: number) => {
    setLoading(true);
    try {
      const response = await NFeFiltrar({ id: nfeId });
      if (response.data.success && response.data.data.length > 0) {
        setNfe(response.data.data[0]);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao carregar dados da NF-e:", error);
      showToast({ message: "Erro ao carregar dados da NF-e", variant: "Danger" });
    } finally {
      setLoading(false);
    }
  };

  const onVoltar = () => {
    navigate("/fiscal/nfe");
  };

  const onBaixarXml = async () => {
    if (!nfe?.id) return;
    setDownloadingXml(true);
    try {
      const nomeArquivo = `NFe_${nfe.numeroNF}`;
      const response = await NFeBuscarXML([nfe.id], nomeArquivo);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nomeArquivo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar XML da NF-e:", error);
      showToast({ message: "Erro ao baixar XML", variant: "Danger" });
    } finally {
      setDownloadingXml(false);
    }
  };

  const onImprimir = async () => {
    if (!nfe?.id) return;
    setPrinting(true);
    try {
      const response = await NFeImprimir(nfe.id);
      if (response.data instanceof Blob) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const isMobile = /Android|iPhone/i.test(navigator.userAgent);

        if (isMobile) {
          window.open(pdfUrl, "_blank");
        } else {
          const iframe = document.createElement("iframe");
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.style.width = "0";
          iframe.style.height = "0";
          iframe.src = pdfUrl;
          document.body.appendChild(iframe);

          iframe.onload = () => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          };
        }
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao imprimir DANFE:", error);
      showToast({ message: "Erro ao imprimir DANFE", variant: "Danger" });
    } finally {
      setPrinting(false);
    }
  };

  const onBaixarDanfe = async () => {
    if (!nfe?.id) return;
    setPrinting(true);
    try {
      const response = await NFeImprimir(nfe.id);
      if (response.data instanceof Blob) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `DanfeNFe${nfe.numeroNF}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfUrl);
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao baixar DANFE:", error);
      showToast({ message: "Erro ao baixar DANFE", variant: "Danger" });
    } finally {
      setPrinting(false);
    }
  };

  const onCompartilharDanfe = async () => {
    if (!nfe?.id) return;
    setPrinting(true);
    try {
      const response = await NFeImprimir(nfe.id);
      if (response.data instanceof Blob) {
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const file = new File([pdfBlob], `DanfeNFe${nfe.numeroNF}.pdf`, {
          type: "application/pdf",
        });

        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            title: "DANFE NF-e",
            files: [file],
          });
        } else {
          onBaixarDanfe();
        }
      } else {
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error) {
      console.error("Erro ao compartilhar DANFE:", error);
      showToast({ message: "Erro ao compartilhar DANFE", variant: "Danger" });
    } finally {
      setPrinting(false);
    }
  };

  const copiarTexto = (texto: string, label = "Texto") => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(texto);
      showToast({
        message: `${label} copiado(a) para a área de transferência!`,
        variant: "Success",
      });
    }
  };

  const toggleItemExpansion = (index: number) => {
    setExpandedItemIndex((prev) => (prev === index ? null : index));
  };

  const formatarDocumento = (doc?: string) => {
    if (!doc) return "-";
    const limpo = SoNumeros(doc);
    if (limpo.length === 11) return FormatarCPF(limpo);
    if (limpo.length === 14) return FormatarCNPJ(limpo);
    return doc;
  };

  const formatarMoeda = (valor?: number) => {
    const num = Number(valor) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const getTipoOperacaoLabel = (tipo?: number) => {
    switch (tipo) {
      case 0:
        return "0 - Entrada";
      case 1:
        return "1 - Saída";
      default:
        return tipo != null ? String(tipo) : "-";
    }
  };

  const getFinalidadeLabel = (finalidade?: number) => {
    switch (finalidade) {
      case 1:
        return "1 - Normal";
      case 2:
        return "2 - Complementar";
      case 3:
        return "3 - Ajuste";
      case 4:
        return "4 - Devolução";
      default:
        return finalidade != null ? String(finalidade) : "-";
    }
  };

  const getDestinoOperacaoLabel = (destino?: number) => {
    switch (destino) {
      case 1:
        return "1 - Operação Interna";
      case 2:
        return "2 - Operação Interestadual";
      case 3:
        return "3 - Operação com Exterior";
      default:
        return destino != null ? String(destino) : "-";
    }
  };

  const getConsumidorFinalLabel = (consumidor?: number) => {
    switch (consumidor) {
      case 0:
        return "0 - Não";
      case 1:
        return "1 - Consumidor Final";
      default:
        return consumidor != null ? String(consumidor) : "-";
    }
  };

  const getModalidadeFreteLabel = (modalidade?: number) => {
    switch (modalidade) {
      case 0:
        return "0 - Por conta do Remetente (CIF)";
      case 1:
        return "1 - Por conta do Destinatário (FOB)";
      case 2:
        return "2 - Por conta de Terceiros";
      case 3:
        return "3 - Próprio por conta do Remetente";
      case 4:
        return "4 - Próprio por conta do Destinatário";
      case 9:
        return "9 - Sem Frete";
      default:
        return modalidade != null ? `Modalidade ${modalidade}` : "-";
    }
  };

  const getMeioPagamentoLabel = (meio?: number, descricaoCustom?: string | null) => {
    if (descricaoCustom) return descricaoCustom;
    const encontrado = listaMeiosPagamento.find((m) => m.meioPagamento === meio);
    return encontrado ? encontrado.descricaoPagamento : meio != null ? `Código ${meio}` : "-";
  };

  const itens = (nfe?.itens || [])
    .slice()
    .sort((a, b) => {
      const seqA =
        a.sequencia != null && a.sequencia > 0
          ? a.sequencia
          : a.index != null && a.index > 0
          ? a.index
          : 0;
      const seqB =
        b.sequencia != null && b.sequencia > 0
          ? b.sequencia
          : b.index != null && b.index > 0
          ? b.index
          : 0;
      return seqA - seqB;
    });

  return {
    nfe,
    itens,
    loading,
    tributacaoNome,
    downloadingXml,
    printing,
    expandedItemIndex,
    onVoltar,
    onBaixarXml,
    onImprimir,
    onBaixarDanfe,
    onCompartilharDanfe,
    copiarTexto,
    toggleItemExpansion,
    formatarDocumento,
    formatarMoeda,
    FormatarCEP,
    FormatarTelefone,
    FormatarDataHoraDefault,
    getTipoOperacaoLabel,
    getFinalidadeLabel,
    getDestinoOperacaoLabel,
    getConsumidorFinalLabel,
    getModalidadeFreteLabel,
    getMeioPagamentoLabel,
  };
};

export default NFeVisualizaCode;
